import { useState, useRef, useEffect } from "react";
import {
  FaBars,
  FaBell,
  FaCheckCircle,
  FaCog,
  FaHistory,
  FaSignOutAlt,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";
import EditProfileModal from "./EditProfileModal";
import { useNavigate } from "react-router-dom";
import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  useUnreadCount,
  useOpenNotification,
} from "../../hooks/useNotficationsQuery";
import { FaUser, FaX } from "react-icons/fa6";
import popupSound from "../../assets/notficationSound.mp3";
import { BiBell } from "react-icons/bi";
import { useQueryClient } from "@tanstack/react-query";
import InvitationDropdown from "../Dropdowns/InvitationDropDown";
import { BsHospital } from "react-icons/bs";
import { usePendingInvitationsCount } from "../../hooks/useInvitationQuery";
import { useIsMobile } from "../../hooks/use-mobile";
import EditSpecProfileModal from "./EditSpecProfileModal";

const Header = ({ toggleSidebar, title, role }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showInvitations, setShowInvitations] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const { mutate: markAllAsRead } = useMarkAllAsRead();
  const markAsRead = useMarkAsRead();
  const { mutate: openNotification } = useOpenNotification();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadCount, refetch: refetchUnreadCount } = useUnreadCount();
  const { data: pendingInvitationsCount = 0 } = usePendingInvitationsCount();

  // Track previous values for change detection
  const previousUnreadCount = useRef(null);
  const previousNotifications = useRef([]);
  const audioRef = useRef(null);
  const dropdownRef = useRef();
  const settingsDropdownRef = useRef();
  const navigate = useNavigate();

  const getNotificationIcon = (type) => {
    const iconClass = "text-xs sm:text-sm lg:text-base";
    switch (type) {
      case "NewPatient":
        return <FaUser className={`text-red-500 ${iconClass}`} />;
      case "BookCreated":
        return <FaCheckCircle className={`text-green-500 ${iconClass}`} />;
      case "BookSkipped":
        return <FaHistory className={`text-yellow-500 ${iconClass}`} />;
      case "BookCanceled":
        return <FaX className={`text-red ${iconClass}`} />;
      case "CenterInvitation":
        return <BsHospital className={`text-primary ${iconClass}`} />;
      default:
        return <BiBell className={`text-primary ${iconClass} mb-2`} />;
    }
  };

  const logOut = async () => {
    try {
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();

      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos) : c;
        document.cookie =
          name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      });

      await queryClient.resetQueries();
      queryClient.removeQueries();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      navigate("/login", { replace: true });
    }
  };

  const handleEditProfile = () => {
    setIsModalOpen(true);
    setShowSettingsDropdown(false);
  };

  const handleLogout = () => {
    logOut();
  };

  // Helper functions for notifications
  const isNotificationSupported = () => {
    return typeof window !== "undefined" && "Notification" in window;
  };

  const requestNotificationPermission = async () => {
    if (!isNotificationSupported()) {
      console.log("Notifications not supported in this environment");
      return "denied";
    }
    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return "denied";
    }
  };

  const showNotification = (title, options) => {
    if (!isNotificationSupported()) {
      console.log("Notifications not supported, skipping notification");
      return null;
    }
    try {
      if (Notification.permission === "granted") {
        return new Notification(title, options);
      }
    } catch (error) {
      console.error("Error showing notification:", error);
    }
    return null;
  };

  // Handle notification dropdown toggle - mark all as read when opening
  const handleNotificationToggle = () => {
    const isOpening = !showNotifications;
    setShowNotifications(isOpening);

    // Mark all as read when opening the dropdown
    if (isOpening && unreadCount > 0) {
      markAllAsRead();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (
        settingsDropdownRef.current &&
        !settingsDropdownRef.current.contains(event.target)
      ) {
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (audioRef.current === null) {
      audioRef.current = new Audio(popupSound);
    }

    if (isNotificationSupported() && Notification.permission === "default") {
      requestNotificationPermission();
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        audioRef.current.load();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (previousUnreadCount.current === null && unreadCount !== undefined) {
      previousUnreadCount.current = unreadCount;
      return;
    }

    // Check if unread count increased (indicates new notifications)
    if (unreadCount !== undefined && previousUnreadCount.current !== null) {
      const countIncreased = unreadCount > previousUnreadCount.current;

      if (countIncreased) {
        console.log(
          `New notifications detected: count increased from ${previousUnreadCount.current} to ${unreadCount}`
        );

        // Check if any new notifications are booking-related when we get fresh data
        // This will trigger automatically due to the useUnreadCount hook invalidating notifications
        if (notifications.length > 0) {
          const newNotifications = notifications.filter(
            (current) =>
              !previousNotifications.current.some(
                (prev) => prev.id === current.id
              )
          );

          const bookingRelatedTypes = [
            "BookCreated",
            "BookSkipped",
            "BookCanceled",
          ];
          const hasBookingNotifications = newNotifications.some(
            (notification) => bookingRelatedTypes.includes(notification.type)
          );

          if (hasBookingNotifications) {
            console.log(
              "New booking-related notification received, refetching bookings..."
            );
            queryClient.invalidateQueries(["bookings"]);
            queryClient.invalidateQueries(["recent-bookings"]);
          }
        }

        // Play sound for new notifications
        try {
          audioRef.current.play().catch(() => {});
        } catch {}

        // Show browser notification if document is hidden
        if (
          document.hidden &&
          isNotificationSupported() &&
          notifications.length > 0
        ) {
          const latest = notifications[0];
          const notification = showNotification(latest.title, {
            body: latest.body,
            icon: "/logo.png",
          });

          if (notification) {
            notification.onclick = () => {
              window.focus();
              notification.close();
            };
          }
        }
      }

      previousUnreadCount.current = unreadCount;
    }
  }, [unreadCount, notifications, queryClient]);

  // Update previous notifications reference when notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      previousNotifications.current = [...notifications];
    }
  }, [notifications]);

  const handleNotificationClick = (notification) => {
    const { id, type } = notification;
    console.log(`Notification clicked: `, notification);

    // Call OpenNotification API endpoint
    openNotification(id);

    // Handle navigation based on type
    switch (type) {
      case "NewPatient":
        navigate("/patients");
        break;
      case "BookCreated":
      case "BookSkipped":
      case "BookCanceled":
        navigate("/bookings");
        break;
      case "CenterInvitation":
        setShowInvitations(true);
        break;
      default:
        break;
    }
    setShowNotifications(false);
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 py-3 sm:py-4 px-2 sm:px-6 w-full flex items-center justify-between relative">
        {/* Left side */}
        <div className="flex items-center min-w-0 flex-1">
          {/* Mobile menu button */}
          <button
            onClick={toggleSidebar}
            className="mr-3 sm:mr-4 text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors md:hidden"
            aria-label="Toggle sidebar"
          >
            <FaBars className="w-5 h-5" />
          </button>

          {/* Title - responsive */}
          <div className="min-w-0 flex-1">
            {typeof title === "string" ? (
              <h1 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 truncate">
                {title}
              </h1>
            ) : (
              <div className="flex items-center">{title}</div>
            )}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center space-x-0 sm:space-x-3 lg:space-x-4 flex-shrink-0">
          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors relative"
              onClick={handleNotificationToggle}
              aria-label="Notifications"
            >
              <FaBell className="w-4 h-4 sm:w-5 sm:h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0 right-1 md:-top-1 md:-right-1 bg-red-500 text-white text-[7px] md:text-xs p-[1px] font-medium rounded-full min-w-[14px] min-h-[14px] md:min-w-[19px] md:min-h-[19px] flex items-center justify-center">
                  {unreadCount > 10 ? "10+" : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div
                className={`absolute right-0 mt-1 bg-white border border-primary/40 rounded-lg shadow-xl z-50 
                                ${
                                  isMobile
                                    ? "w-64 max-w-[calc(100vw-2rem)]"
                                    : "w-80 lg:w-96"
                                }`}
              >
                <div className="absolute -top-[6px] right-2 w-3 h-3 bg-primary transform -rotate-45 border-r border-b border-primary"></div>
                {/* Header */}
                <div className="border-b rounded-t-lg px-4 sm:px-5 py-3 flex justify-between items-center bg-primary">
                  <h3 className="text-sm md:text-base font-semibold text-white">
                    Notifications
                  </h3>
                  <button
                    onClick={() => markAllAsRead()}
                    className="text-xs sm:text-sm text-white/90 hover:text-white font-medium hover:underline"
                  >
                    Mark all as read
                  </button>
                </div>

                {/* Content */}
                <div className="p-2 sm:p-4 max-h-80 sm:max-h-96 overflow-y-auto">
                  <p className="text-xs sm:text-sm text-gray-500 mb-3">
                    Earlier
                  </p>
                  {isLoading ? (
                    <p className="text-center text-gray-400 text-sm py-8">
                      Loading notifications...
                    </p>
                  ) : notifications.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-8">
                      No notifications
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={`flex gap-3 items-start p-3 border rounded-lg transition-all cursor-pointer
                                                        ${
                                                          !n.isOpened
                                                            ? "bg-blue-50 border-blue-200"
                                                            : "bg-white border-gray-100"
                                                        } 
                                                        hover:bg-blue-100 hover:border-blue-300`}
                        >
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            {getNotificationIcon(n.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs sm:text-base text-gray-800 ${
                                !n.isRead ? "font-semibold" : ""
                              }`}
                            >
                              {n.title}
                            </p>
                            <p className="text-[12px] sm:text-sm text-gray-600 mt-1">
                              {n.body}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-1">
                              {new Date(n.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {!n.isOpened && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Invitations */}
          {/* <div className="relative">
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-all duration-200 group relative mt-1"
              onClick={() => setShowInvitations(!showInvitations)}
              aria-label="Invitations"
            >
              <FaUsers className="w-5 h-5 sm:w-5 sm:h-5 transition-transform group-hover:scale-110" />
              {pendingInvitationsCount > 0 && (
                <span className="absolute -top-0 right-1 md:-top-1 md:-right-1 bg-primary animate-pulse text-white text-[7px] md:text-xs p-[1px] font-medium rounded-full min-w-[14px] min-h-[14px] md:min-w-[19px] md:min-h-[19px] flex items-center justify-center">
                  {pendingInvitationsCount > 99
                    ? "99+"
                    : pendingInvitationsCount}
                </span>
              )}
            </button>
          </div> */}

          {/* <InvitationDropdown
            isOpen={showInvitations}
            setIsOpen={setShowInvitations}
          /> */}

          {/* Settings Dropdown */}
          <div className="relative" ref={settingsDropdownRef}>
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              aria-label="Settings"
            >
              <FaCog className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Settings Dropdown Menu */}
            {showSettingsDropdown && (
              <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[160px]">
                <div className="absolute -top-[6px] right-4 w-3 h-3 bg-white transform rotate-45 border-l border-t border-gray-200"></div>

                <div className="py-2">
                  {/* Edit Profile */}
                  <button
                    onClick={handleEditProfile}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center gap-3"
                  >
                    <FaUser className="w-4 h-4 text-gray-500" />
                    Edit Profile
                  </button>

                  {/* Divider */}
                  <div className="border-t border-gray-100 my-1"></div>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-3"
                  >
                    <FaSignOutAlt className="w-4 h-4 text-red-500" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {role == 2 ? (
        <EditProfileModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      ) : (
        <EditSpecProfileModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
